/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Stack } from '@mui/material';
import React from 'react';
import { Sources, Layers, Location, Equalizer } from '@opengeoweb/theme';
import { SoundingsMarkersSwitch } from './SoundingsMarkersSwitch';
import { SoundingsTopRowButton } from './SoundingsTopRowButton';
import { useSoundingsTranslation } from '../../utils/i18n';

export enum MenuOptions {
  soundingsManager = 'Sd Manager',
  sources = 'sources',
  locations = 'locations',
  profileData = 'profileData',
}
interface SoundingsMenuBarProps {
  handleClick: (menuType: MenuOptions) => void;
  isOpen: Record<MenuOptions, boolean>;
  selectedSourcesAmount?: number;
  shouldHideMarkers: boolean;
  toggleMarkers: (shouldHideMarkers: boolean) => void;
}

export const SoundingsMenuBar = ({
  handleClick,
  isOpen,
  selectedSourcesAmount = 0,
  shouldHideMarkers,
  toggleMarkers,
}: SoundingsMenuBarProps): React.ReactElement => {
  const { t } = useSoundingsTranslation();
  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        marginBottom: '4px',
      }}
    >
      <SoundingsTopRowButton
        data-testid="soundingsManagerButton"
        icon={<Layers />}
        longText={MenuOptions.soundingsManager}
        onClick={() => handleClick(MenuOptions.soundingsManager)}
        selected={isOpen[MenuOptions.soundingsManager]}
        size="small"
      />

      <SoundingsTopRowButton
        data-testid="soundingsSourcesButton"
        icon={<Sources sx={{ marginRight: '4px!important' }} />}
        longText={
          selectedSourcesAmount
            ? `${t('sources')} (${selectedSourcesAmount})`
            : t('sources')
        }
        onClick={() => handleClick(MenuOptions.sources)}
        selected={isOpen[MenuOptions.sources]}
        size="small"
      />
      <SoundingsTopRowButton
        data-testid="soundingsLocationsButton"
        icon={<Location />}
        longText={t('locations')}
        onClick={() => handleClick(MenuOptions.locations)}
        selected={isOpen[MenuOptions.locations]}
        size="small"
      />
      <SoundingsTopRowButton
        data-testid="soundingsProfileDataButton"
        icon={<Equalizer />}
        longText="Profile Data"
        onClick={() => handleClick(MenuOptions.profileData)}
        selected={isOpen[MenuOptions.profileData]}
        size="small"
      />
      <SoundingsMarkersSwitch
        shouldHideMarkers={shouldHideMarkers}
        toggleMarkers={toggleMarkers}
      />
    </Stack>
  );
};
